import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { globalSEOService } from "../../services/globalSeoService";

// Define OpenGraph structure based on Strapi schema
interface OpenGraph {
  "og:title"?: string;
  "og:description"?: string;
  "og:image"?: {
    url?: string;
    formats?: {
      small?: { url?: string };
      medium?: { url?: string };
      thumbnail?: { url?: string };
    };
  };
  "og:url"?: string;
  "og:type"?: string;
  // Also support Strapi's direct field names (without "og:" prefix)
  title?: string;
  description?: string;
  image?:
    | {
        url?: string;
        formats?: {
          small?: { url?: string };
          medium?: { url?: string };
          thumbnail?: { url?: string };
        };
      }
    | string;
  url?: string;
  type?: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  schema?: object;
  openGraph?: OpenGraph; // OpenGraph data from Strapi
  seoData?: any; // Full SEO data object
  children?: React.ReactNode;
  author?: string; // Add author prop
  publisher?: string; // Add publisher prop
  useGlobalFallbacks?: boolean; // New prop to enable global SEO fallbacks
}

const SITE_NAME = "Evefant"; // You can make this configurable
const DEFAULT_DESCRIPTION =
  "Encuentra todo para tu fiesta y nosotros te lo llevamos. Desde decoración con globos, hasta los bocaditos. Delivery en todo Lima."; // You can make this configurable
const DEFAULT_IMAGE =
  "https://media.evefant.com/isotipo_Evefant_azul_b2dbc10d2e.webp"; // Default OG image
const DEFAULT_AUTHOR = "Evefant"; // Default author
const DEFAULT_PUBLISHER = "Evefant"; // Default publisher

// Helper function to treat null/undefined/empty string as missing
const safeValue = (value: any): any => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return value;
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  type,
  schema,
  openGraph,
  seoData,
  children,
  author,
  publisher,
  useGlobalFallbacks = false,
}) => {
  const [globalSEO, setGlobalSEO] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(useGlobalFallbacks);

  // Fetch global SEO data if fallbacks are enabled
  useEffect(() => {
    if (useGlobalFallbacks) {
      globalSEOService
        .getDefaultSEO()
        .then((data) => {
          setGlobalSEO(data);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Failed to load global SEO data:", error);
          setIsLoading(false);
        });
    }
  }, [useGlobalFallbacks]);

  // Don't render anything while loading global data
  if (isLoading) {
    return null;
  }

  // Prefer backend SEO data if available, then global SEO, then props, then defaults
  const backendSEO = seoData || {};
  const og = openGraph || backendSEO.openGraph || {};

  // Debug logging to understand the data structure
  if (backendSEO.openGraph || openGraph) {
    console.log("🔍 SEO Debug - OpenGraph data received:", {
      openGraphProp: openGraph,
      backendOpenGraph: backendSEO.openGraph,
      combinedOg: og,
    });
  }

  // Build final values with fallback chain: prioritize explicit props (which contain backend SEO data)
  // Treat null values as missing data
  // Handle both "og:title" format and "title" format from Strapi
  const currentOgTitle = String(
    safeValue(title) ||
      safeValue(backendSEO.metaTitle) ||
      safeValue(backendSEO.title) ||
      safeValue(og["og:title"]) ||
      safeValue(og.title) ||
      safeValue(globalSEO?.title) ||
      SITE_NAME
  );

  const currentOgDescription = String(
    safeValue(description) ||
      safeValue(backendSEO.metaDescription) ||
      safeValue(backendSEO.description) ||
      safeValue(og["og:description"]) ||
      safeValue(og.description) ||
      safeValue(globalSEO?.description) ||
      DEFAULT_DESCRIPTION
  );

  const currentOgImage = String(
    safeValue(og["og:image"]?.url) ||
      safeValue(og.image?.url) ||
      safeValue(og.image) ||
      safeValue(og["og:image"]) ||
      safeValue(backendSEO.metaImage?.url) ||
      safeValue(backendSEO.image?.url) ||
      safeValue(backendSEO.image) ||
      safeValue(globalSEO?.image) ||
      safeValue(image) ||
      DEFAULT_IMAGE
  );

  const currentOgUrl = String(
    safeValue(og["og:url"]) ||
      safeValue(og.url) ||
      safeValue(backendSEO.canonicalURL) ||
      safeValue(backendSEO.url) ||
      safeValue(globalSEO?.canonicalUrl) ||
      (typeof window !== "undefined" ? window.location.href : "")
  );

  const currentOgType = String(
    safeValue(og["og:type"]) ||
      safeValue(og.type) ||
      safeValue(backendSEO.type) ||
      safeValue(type) ||
      "website"
  );

  // Debug logging for final computed values
  console.log("🔍 SEO Debug - Final computed values:", {
    title: currentOgTitle,
    description: currentOgDescription,
    image: currentOgImage,
    url: currentOgUrl,
    type: currentOgType,
  });

  const currentMetaAuthor = String(
    safeValue(backendSEO.author) ||
      safeValue(globalSEO?.author) ||
      safeValue(author) ||
      DEFAULT_AUTHOR
  );

  const currentMetaPublisher = String(
    safeValue(backendSEO.publisher) ||
      safeValue(globalSEO?.publisher) ||
      safeValue(publisher) ||
      DEFAULT_PUBLISHER
  );

  // Enhanced keywords with global fallbacks
  const currentKeywords = String(
    safeValue(backendSEO.keywords) ||
      safeValue(globalSEO?.keywords) ||
      "evefant, celebraciones, fiestas, delivery, decoración, globos, bocaditos, lima"
  );

  // Ensure absolute image URL
  const absoluteImageUrl = String(
    currentOgImage && currentOgImage.startsWith("http")
      ? currentOgImage
      : typeof window !== "undefined"
      ? `${window.location.origin}${
          currentOgImage.startsWith("/") ? "" : "/"
        }${currentOgImage}`
      : currentOgImage
  );

  // Structured data - treat null as missing
  const structuredData = safeValue(backendSEO.structuredData) || schema;

  const pageTitle = currentOgTitle ? `${currentOgTitle}` : SITE_NAME;

  return (
    <Helmet htmlAttributes={{ lang: "es" }}>
      {/* Basic tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={currentOgDescription} />
      <meta name="author" content={currentMetaAuthor} />
      <meta name="publisher" content={currentMetaPublisher} />

      {/* Canonical URL */}
      <link rel="canonical" href={currentOgUrl} />

      {/* Robots tag (allow indexing by default) */}
      <meta
        name="robots"
        content={
          safeValue(backendSEO.metaRobots) ||
          (safeValue(backendSEO.noIndex) || safeValue(globalSEO?.noIndex)
            ? "noindex,nofollow"
            : "index,follow")
        }
      />

      {/* Favicon (optional, but recommended for SEO/UX) */}
      <link rel="icon" href="/favicon.ico" />

      {/* OpenGraph tags */}
      <meta property="og:title" content={currentOgTitle} />
      <meta property="og:description" content={currentOgDescription} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:url" content={currentOgUrl} />
      <meta property="og:type" content={currentOgType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_PE" />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentOgTitle} />
      <meta name="twitter:description" content={currentOgDescription} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      {/* Optionally add twitter:site and twitter:creator if you have them */}
      {/* <meta name="twitter:site" content="@yourbrand" /> */}
      {/* <meta name="twitter:creator" content="@author" /> */}

      {/* Preload main image for CLS optimization - only if it's actually displayed on the page */}
      {/* Note: We don't preload og:image as it's only used by social media crawlers, not displayed on page */}

      {/* Mobile tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#000000" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Language (alternate) */}
      <link rel="alternate" hrefLang="es" href={currentOgUrl} />

      {/* Schema.org JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {(() => {
            try {
              // Handle both string (from backend) and object cases
              if (typeof structuredData === "string") {
                // Backend already returns stringified JSON, use as-is
                return structuredData;
              } else {
                // Object case, stringify it
                return JSON.stringify(structuredData);
              }
            } catch (error) {
              console.error("Error processing structured data:", error);
              return JSON.stringify({
                "@context": "https://schema.org/",
                "@type": "WebPage",
                name: String(title || "Evefant"),
              });
            }
          })()}
        </script>
      )}

      {/* Enhanced keywords meta tag */}
      <meta name="keywords" content={currentKeywords} />

      {/* Additional tags */}
      {children}
    </Helmet>
  );
};

export default SEO;
